import axios, { AxiosInstance } from 'axios';
import { createApiClient } from './http/client';
import {
  AperoSyncClinicsResponse,
  BillingTransform,
  EHRClaimSync,
  EHRClaimSyncRequestPayload,
  EHRClaimSyncRequestResults,
  EHRSyncPayload,
  EhrClaimOverrides,
  EhrClaimResponse,
  EhrClaimResyncPayload,
  EhrClaimsAgingParams,
  EhrClaimsAgingResponse,
  EhrClaimsErrorSummaryParams,
  EhrClaimsErrorSummaryResponse,
  EhrClaimsMetricsParams,
  EhrClaimsMetricsResponse,
  EhrClaimsResponse,
  ExportedClaimsFilters,
  GetAllClaimsParams,
  ProvidersResponse,
  RpmCalculationFilters,
  RpmExportedFilters,
  RpmPatientSelectionFilters,
} from '../interfaces/billing';
import {
  AsyncExportResponse,
  AsyncExportStatusResponse,
  BillingExportDetailResponse,
  BillingExportStatsResponse,
  GetBillingExportStatsParams,
  ListBillingExportRowsParams,
  ListBillingExportRowsResponse,
  ListBillingExportsParams,
  ListBillingExportsResponse,
} from '../interfaces/billing.async';
import {
  BackgroundProcessResponse,
  BillingClaimLogResponse,
  BillingClaimsExportedResponse,
  BillingClaimsPreviewResponse,
  BillingNoteResponse,
  BillingPatientsResponse,
  BillingProcess,
  BillingUser,
  ClinicConfig,
  ClinicConfigUpdate,
  ClinicsConfigurationResponse,
  CsvRawContent,
  GetAllClaimsResponse,
  GetNoteExportsParams,
  NoteExportsResponse,
  ProcessApiResponse,
  ProcessExecutionConfig,
  RPMNote,
  SyncProcessResponse,
} from '../interfaces/billing.rpm';
import { DEFAULT_PAGE_SIZE } from '../util/constants';
import { AuthService } from './auth';

const ENARA_API_URL = process.env.REACT_APP_ENARA_BASE_URL;
const ENARA_CORE_URL = process.env.REACT_APP_ENARA_URL;
const BILLING_API_URL = process.env.REACT_APP_PUBLIC_BILLING_API_BASE_URL;

export class BillingService {
  private legacyServiceAxiosClient: AxiosInstance;
  private axiosClient: AxiosInstance;

  constructor() {
    this.legacyServiceAxiosClient = createApiClient({
      baseURL: `${ENARA_API_URL}/billing`,
      timeout: 0,
    });
    this.axiosClient = createApiClient({
      baseURL: `${BILLING_API_URL}`,
      timeout: 0,
    });
  }

  public async getTransformedFile(
    data: FormData,
    clinicId: number,
    isHL7: boolean,
  ): Promise<BillingTransform[] | null> {
    const authData = AuthService.getAuth();
    try {
      const response = await this.legacyServiceAxiosClient.post('/billing/v1/transform', data, {
        headers: {
          Authorization: `Bearer ${authData?.auth_token}`,
          'content-type': 'multipart/form-data',
          'Clinic-Id': `${clinicId}`,
          ...(isHL7 && { Hl7: `${isHL7}` }),
        },
      });

      return response.data;
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  public async runEhrClaimSync(payload: EHRSyncPayload): Promise<AperoSyncClinicsResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await this.legacyServiceAxiosClient.post<AperoSyncClinicsResponse>(
        '/billing/v1/ehr-claims-syncs',
        payload,
        { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
      );

      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public async reSyncEhrClaim(
    claimId: number,
    payload?: EhrClaimResyncPayload,
  ): Promise<EhrClaimResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await this.legacyServiceAxiosClient.post<EhrClaimResponse>(
        `/billing/v1/ehr-claims/${claimId}/syncs`,
        payload,
        { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
      );

      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public async getClaimSyncRequests(
    payload: EHRClaimSyncRequestPayload,
  ): Promise<EHRClaimSyncRequestResults | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await this.legacyServiceAxiosClient.get(
        '/billing/v1/ehr-claim-sync-requests',
        {
          headers: { Authorization: `Bearer ${authData?.auth_token}` },
          params: payload,
        },
      );

      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public async getClaims(payload: EHRClaimSync): Promise<EhrClaimsResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await this.legacyServiceAxiosClient.get('/billing/v1/ehr-claims-syncs', {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
        params: payload,
      });

      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public async getClaimsMetrics(
    params: EhrClaimsMetricsParams,
  ): Promise<EhrClaimsMetricsResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await this.legacyServiceAxiosClient.get<EhrClaimsMetricsResponse>(
        '/billing/v1/ehr-claims/metrics',
        {
          headers: { Authorization: `Bearer ${authData?.auth_token}` },
          params,
        },
      );

      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public async getClaimsErrorSummary(
    params: EhrClaimsErrorSummaryParams,
  ): Promise<EhrClaimsErrorSummaryResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await this.legacyServiceAxiosClient.get<EhrClaimsErrorSummaryResponse>(
        '/billing/v1/ehr-claims/errors',
        {
          headers: { Authorization: `Bearer ${authData?.auth_token}` },
          params,
        },
      );

      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public async getClaimsAging(
    params: EhrClaimsAgingParams,
  ): Promise<EhrClaimsAgingResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await this.legacyServiceAxiosClient.get<EhrClaimsAgingResponse>(
        '/billing/v1/ehr-claims/aging',
        {
          headers: { Authorization: `Bearer ${authData?.auth_token}` },
          params,
        },
      );

      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public async getClaim(id: number): Promise<EhrClaimResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await this.legacyServiceAxiosClient.get(`/billing/v1/ehr-claims/${id}`, {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
      });

      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public async deleteEhrClaim(
    id: number,
  ): Promise<{ success: boolean; message?: string } | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await this.legacyServiceAxiosClient.delete<{
        success: boolean;
        message?: string;
      }>(`/billing/v1/ehr-claims/${id}`, {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
      });

      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public async updateClaimOverrides(
    claimId: number,
    overrides: EhrClaimOverrides,
  ): Promise<EhrClaimResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await this.legacyServiceAxiosClient.patch<EhrClaimResponse>(
        `/billing/v1/ehr-claims/${claimId}/overrides`,
        overrides,
        { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
      );

      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public async getProvidersByClinic(clinicId: number): Promise<ProvidersResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await axios.post<ProvidersResponse>(
        `${ENARA_CORE_URL}/api/v4/providers/searches`,
        { clinic_id: clinicId, per_page: 100 },
        {
          headers: { Authorization: `Bearer ${authData?.auth_token}` },
        },
      );

      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public async getPatients({
    clinicIds = [],
    optOutStatus = [],
    icd10Codes = [],
    insuranceCompanyIds = [],
    cptCodes = [],
    rpmStatuses = [],
    memberIds = [],
    limit = 10,
    page = 1,
  }: RpmPatientSelectionFilters): Promise<BillingPatientsResponse | null> {
    const authData = AuthService.getAuth();

    try {
      // removing empty array and empty items.
      const parameters = this.prepareParameters({
        clinicIds,
        optOutStatus,
        icd10Codes,
        insuranceCompanyIds,
        cptCodes,
        rpmStatuses,
        memberUuids: memberIds,
        limit,
        page,
      });

      const response = await this.axiosClient.get<BillingPatientsResponse>('/patients', {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
        params: parameters,
      });

      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public async generateClaimsBulk(
    memberUuids: Partial<{ select: string[]; exclude: string[] }>,
    filters: Partial<{
      clinicIds: number[];
      icd10Codes: string[];
      insuranceCompanyIds: string[];
      memberUuids: string[];
      optOutStatus: string[];
      rpmStatuses: string[];
      cptCodes: string[];
    }>,
    user: BillingUser,
    config?: ProcessExecutionConfig,
  ): Promise<ProcessApiResponse> {
    const authData = AuthService.getAuth();
    const body = {
      ...(config && { config }),
      ...(Object.keys(filters).length && { filters }),
      ...(memberUuids.select && {
        select: memberUuids.select.map((memberUuid) => ({ memberUuid })),
      }),
      ...(memberUuids.exclude && {
        exclude: memberUuids.exclude.map((memberUuid) => ({ memberUuid })),
      }),
      user,
    };

    try {
      const response = await this.axiosClient.post<ProcessApiResponse>('/claims/bulk', body, {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
        timeout: config?.runInBackground ? 30000 : 90000, // Shorter timeout for async requests
      });

      return response.data;
    } catch (error) {
      console.error('Error generating claims bulk:', error);
      throw error;
    }
  }

  // Legacy method for backward compatibility
  public async generateClaimsBulkLegacy(
    memberUuids: Partial<{ select: string[]; exclude: string[] }>,
    filters: Partial<{
      clinicIds: number[];
      icd10Codes: string[];
      insuranceCompanyIds: string[];
      memberUuids: string[];
      optOutStatus: string[];
      rpmStatuses: string[];
      cptCodes: string[];
    }>,
    user: BillingUser,
  ) {
    const response = await this.generateClaimsBulk(memberUuids, filters, user);

    // Return legacy format for backward compatibility
    const processFromResponse = this.getProcessFromResponse(response);
    if (processFromResponse) {
      return processFromResponse;
    }

    return response;
  }

  public async exportClaims(
    claimIds: Partial<{ select: number[]; exclude: number[] }>,
    filters: Partial<{
      clinicIds: number[];
      insuranceCompanyIds: string[];
      dateOfService: string;
      memberUuids: string[];
      processId: number;
      cptCodes: string[];
    }>,
    user: BillingUser,
    config?: ProcessExecutionConfig,
  ): Promise<ProcessApiResponse> {
    const authData = AuthService.getAuth();
    const body = {
      ...(config && { config }),
      ...(Object.keys(filters).length && { filters }),
      ...(claimIds.select && {
        select: claimIds.select.map((id) => ({ id })),
      }),
      ...(claimIds.exclude && {
        exclude: claimIds.exclude.map((id) => ({ id })),
      }),
      user,
    };

    try {
      const response = await this.axiosClient.post<ProcessApiResponse>('/claims/export', body, {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
        timeout: config?.runInBackground ? 30000 : 90000, // Shorter timeout for async requests
      });

      return response.data;
    } catch (error) {
      console.error('Error exporting claims:', error);
      throw error;
    }
  }

  // Legacy method for backward compatibility
  public async exportClaimsLegacy(
    claimIds: Partial<{ select: number[]; exclude: number[] }>,
    filters: Partial<{
      clinicIds: number[];
      insuranceCompanyIds: string[];
      dateOfService: string;
      memberUuids: string[];
      processId: number;
      cptCodes: string[];
    }>,
    user: BillingUser,
  ) {
    const response = await this.exportClaims(claimIds, filters, user);

    // Return legacy format for backward compatibility
    const processFromResponse = this.getProcessFromResponse(response);
    if (processFromResponse) {
      return processFromResponse;
    }

    return response;
  }

  public async getClaimsCalculated(
    parameters: RpmCalculationFilters,
  ): Promise<BillingClaimsPreviewResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const finalParameters = this.prepareParameters(parameters);

      const response = await this.axiosClient.get<BillingClaimsPreviewResponse>('/claims/preview', {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
        params: finalParameters,
      });

      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public async getExportedClaims(
    filters: RpmExportedFilters | ExportedClaimsFilters,
  ): Promise<BillingClaimsExportedResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const finalParameters = this.prepareParameters(filters);

      const response = await this.axiosClient.get<BillingClaimsExportedResponse>(
        '/claims/exported',
        {
          headers: { Authorization: `Bearer ${authData?.auth_token}` },
          params: finalParameters,
        },
      );

      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /**
   * NEW: Unified endpoint to get all claims (created, exported, discarded)
   * Supports comprehensive filtering, sorting, and server-side pagination
   */
  public async getAllClaims(params: GetAllClaimsParams): Promise<GetAllClaimsResponse | null> {
    const authData = AuthService.getAuth();

    try {
      // Use prepareParameters to clean up the params object
      const cleanedParams = this.prepareParameters(params);

      const response = await this.axiosClient.get<GetAllClaimsResponse>('/claims', {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
        params: cleanedParams,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        throw error;
      }
      console.error('Error fetching all claims:', error);
      return null;
    }
  }

  public async discardClaims(processId: number, user: BillingUser) {
    const authData = AuthService.getAuth();

    try {
      const response = await this.axiosClient.patch(
        '/claims/discard',
        { processId, user },
        {
          headers: { Authorization: `Bearer ${authData?.auth_token}` },
        },
      );

      if (response.status !== 200) {
        const errorMessage = `Can't discard claims linked to process ID=${processId}`;
        console.log(errorMessage);
        throw new Error(errorMessage);
      }

      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  private prepareParameters<T extends Record<string, any>>(input: T): T {
    return Object.fromEntries(
      Object.entries(input).filter(([_, value]) => {
        if (Array.isArray(value)) {
          if (value.length === 0) {
            return false;
          }
          return value.every((item) => item !== undefined && item !== null && item !== '');
        }

        if (typeof value === 'object' && value !== null && Object.keys(value).length === 0) {
          return false;
        }

        return value !== undefined;
      }),
    ) as T;
  }

  public async getRpmNote(claimId: number): Promise<BillingNoteResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await this.axiosClient.get<BillingNoteResponse>(`/claims/${claimId}/note`, {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
      });

      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public async fetchSignedUrl(claimId: number): Promise<string | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await this.axiosClient.get<{ url: string }>(`/claims/${claimId}/note/pdf`, {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
      });

      return response.data.url;
    } catch (error) {
      console.error(`Error fetching signed URL for claim ${claimId}:`, error);
      return null;
    }
  }

  public async getClaimLogs(
    claimId: number,
    pagination: { page: number; limit: number },
  ): Promise<BillingClaimLogResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await this.axiosClient.get<BillingClaimLogResponse>(
        `/claims/${claimId}/logs`,
        {
          headers: { Authorization: `Bearer ${authData?.auth_token}` },
          params: pagination,
        },
      );

      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public async getProcesses(): Promise<BillingProcess[] | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await this.axiosClient.get<BillingProcess[]>('/processes', {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
      });
      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public async getNoteExports(params?: GetNoteExportsParams): Promise<NoteExportsResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await this.axiosClient.get<NoteExportsResponse>('/notes/exports', {
        params,
        headers: {
          Authorization: `Bearer ${authData?.auth_token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching note exports:', error);
      return null;
    }
  }

  public async getNoteExportDownloadUrl(exportId: number): Promise<string | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await this.axiosClient.get<{ url: string }>(
        `/notes/exports/${exportId}/download-url`,
        {
          headers: {
            Authorization: `Bearer ${authData?.auth_token}`,
          },
        },
      );

      return response.data.url;
    } catch (error) {
      console.error(`Error fetching pre-signed URL for exportId ${exportId}:`, error);
      return null;
    }
  }

  public async exportNotes(
    claimIds: Partial<{ select: number[]; exclude: number[] }>,
    filters: Partial<{
      clinicIds: number[];
      insuranceCompanyIds: string[];
      dateOfService: string;
      cptCodes: string[];
      memberUuids: string[];
      processId: number;
      invoiceCreationDateFrom: string;
      invoiceCreationDateTo: string;
      statuses: string[];
    }>,
    user: BillingUser,
    config?: ProcessExecutionConfig,
  ): Promise<ProcessApiResponse> {
    const authData = AuthService.getAuth();
    const body = this.prepareParameters({
      ...(config && { config }),
      ...(Object.keys(filters).length && { filters }),
      ...(claimIds.select && {
        select: claimIds.select.map((id) => ({ id })),
      }),
      ...(claimIds.exclude && {
        exclude: claimIds.exclude.map((id) => ({ id })),
      }),
      user,
    });

    try {
      const response = await this.axiosClient.post<ProcessApiResponse>('/notes/export', body, {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
        timeout: config?.runInBackground ? 30000 : 90000, // Shorter timeout for async requests
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting notes:', error);
      throw error;
    }
  }

  public async getAllClinicsConfiguration(
    page: number = 1,
    limit: number = DEFAULT_PAGE_SIZE,
  ): Promise<ClinicsConfigurationResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await this.axiosClient.get<ClinicsConfigurationResponse>(
        '/clinic-payer-config',
        {
          headers: {
            Authorization: `Bearer ${authData?.auth_token}`,
          },
          params: { page, limit },
        },
      );

      return response.data;
    } catch (error) {
      console.error('Error getting clinic config:', error);
      return null;
    }
  }

  public async addClinicConfiguration(clinic: ClinicConfig) {
    const authData = AuthService.getAuth();

    const body = this.prepareParameters(clinic);
    try {
      const response = await this.axiosClient.post<BillingProcess[]>('/clinic-payer-config', body, {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
      });

      return response.data;
    } catch (error) {
      console.error('Error adding clinic config:', error);
      return null;
    }
  }

  public async updateClinicConfiguration(clinicId: number, toUpdate: ClinicConfigUpdate) {
    const authData = AuthService.getAuth();
    const body = this.prepareParameters(toUpdate);

    try {
      const response = await this.axiosClient.put(`/clinic-payer-config/${clinicId}`, body, {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
      });

      return response.data;
    } catch (error) {
      console.error('Error updating clinic config:', error);
      return null;
    }
  }

  public async exportCSVFile(
    clinicId: number | null,
    processId: number | null,
  ): Promise<CsvRawContent | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await this.axiosClient.get<CsvRawContent>('/claims/export/mankato/csv', {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
        params: { clinicId, processId },
      });

      return response.data;
    } catch (error) {
      console.error(error);
      throw new Error((error as Error).message);
    }
  }

  public async updateNoteContent(
    noteId: number,
    content: { header: string; body: string; footer: string },
    user: BillingUser,
  ) {
    const authData = AuthService.getAuth();

    const response = await this.axiosClient.patch<RPMNote>(
      `/notes/${noteId}/content`,
      { content, user },
      {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
      },
    );

    return response.data;
  }

  public async resetNoteContent(noteId: number, user: BillingUser) {
    const authData = AuthService.getAuth();

    const response = await this.axiosClient.patch<RPMNote>(
      `/notes/${noteId}/content/reset`,
      { user },
      {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
      },
    );
    return response.data;
  }

  // Utility methods for response type checking
  public isBackgroundResponse(response: ProcessApiResponse): response is BackgroundProcessResponse {
    return 'mode' in response && response.mode === 'background';
  }

  public isSyncResponse(response: ProcessApiResponse): response is SyncProcessResponse {
    return 'processId' in response && !('mode' in response);
  }

  public getProcessId(response: ProcessApiResponse): number {
    if (this.isBackgroundResponse(response)) {
      return response.process.id;
    }
    return response.processId;
  }

  public getProcessFromResponse(response: ProcessApiResponse): BillingProcess | null {
    if (this.isBackgroundResponse(response)) {
      return response.process;
    }
    return null;
  }

  public createProcessSummary(response: ProcessApiResponse): string {
    if (this.isSyncResponse(response)) {
      const { created, failed, exported } = response;
      if (created !== undefined) {
        return `Created: ${created}${failed ? `, Failed: ${failed}` : ''}`;
      } else if (exported !== undefined) {
        return `Exported: ${exported}${failed ? `, Failed: ${failed}` : ''}`;
      }
    }
    return '';
  }

  // =====================================================
  // Async Billing Export Methods
  // =====================================================

  /**
   * Start an async export transformation
   * POST /billing/v1/transform-async
   */
  public async startAsyncExport(
    data: FormData,
    clinicId: number,
    isHL7: boolean,
  ): Promise<AsyncExportResponse | null> {
    const authData = AuthService.getAuth();
    try {
      const response = await this.legacyServiceAxiosClient.post<AsyncExportResponse>(
        '/billing/v1/transform-async',
        data,
        {
          headers: {
            Authorization: `Bearer ${authData?.auth_token}`,
            'content-type': 'multipart/form-data',
            'Clinic-Id': `${clinicId}`,
            ...(isHL7 && { Hl7: `${isHL7}` }),
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error('Error starting async export:', error);
      return null;
    }
  }

  /**
   * Get the status of an async export
   * GET /billing/v1/export-status/{requestId}
   */
  public async getExportStatus(requestId: string): Promise<AsyncExportStatusResponse | null> {
    const authData = AuthService.getAuth();
    try {
      const response = await this.legacyServiceAxiosClient.get<AsyncExportStatusResponse>(
        `/billing/v1/export-status/${requestId}`,
        {
          headers: {
            Authorization: `Bearer ${authData?.auth_token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error('Error getting export status:', error);
      return null;
    }
  }

  /**
   * List billing exports with pagination and filters
   * GET /billing/v1/exports
   */
  public async listBillingExports(
    clinicId: number,
    params?: ListBillingExportsParams,
  ): Promise<ListBillingExportsResponse | null> {
    const authData = AuthService.getAuth();
    try {
      const response = await this.legacyServiceAxiosClient.get<ListBillingExportsResponse>(
        '/billing/v1/exports',
        {
          headers: {
            Authorization: `Bearer ${authData?.auth_token}`,
            'Clinic-Id': `${clinicId}`,
          },
          params: params ? this.prepareParameters(params) : undefined,
        },
      );

      return response.data;
    } catch (error) {
      console.error('Error listing billing exports:', error);
      return null;
    }
  }

  /**
   * Get detailed information about a specific export
   * GET /billing/v1/exports/{requestId}
   */
  public async getBillingExportDetail(
    requestId: string,
  ): Promise<BillingExportDetailResponse | null> {
    const authData = AuthService.getAuth();
    try {
      const response = await this.legacyServiceAxiosClient.get<BillingExportDetailResponse>(
        `/billing/v1/exports/${requestId}`,
        {
          headers: {
            Authorization: `Bearer ${authData?.auth_token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error('Error getting billing export detail:', error);
      return null;
    }
  }

  /**
   * Get rows for a specific export with pagination and filters
   * GET /billing/v1/exports/{requestId}/rows
   */
  public async getBillingExportRows(
    requestId: string,
    params?: ListBillingExportRowsParams,
  ): Promise<ListBillingExportRowsResponse | null> {
    const authData = AuthService.getAuth();
    try {
      const response = await this.legacyServiceAxiosClient.get<ListBillingExportRowsResponse>(
        `/billing/v1/exports/${requestId}/rows`,
        {
          headers: {
            Authorization: `Bearer ${authData?.auth_token}`,
          },
          params: params ? this.prepareParameters(params) : undefined,
        },
      );

      return response.data;
    } catch (error) {
      console.error('Error getting billing export rows:', error);
      return null;
    }
  }

  /**
   * Get export statistics for a clinic
   * GET /billing/v1/exports/stats
   */
  public async getBillingExportStats(
    clinicId: number,
    params?: GetBillingExportStatsParams,
  ): Promise<BillingExportStatsResponse | null> {
    const authData = AuthService.getAuth();
    try {
      const response = await this.legacyServiceAxiosClient.get<BillingExportStatsResponse>(
        '/billing/v1/exports/stats',
        {
          headers: {
            Authorization: `Bearer ${authData?.auth_token}`,
            'Clinic-Id': `${clinicId}`,
          },
          params: params ? this.prepareParameters(params) : undefined,
        },
      );

      return response.data;
    } catch (error) {
      console.error('Error getting billing export stats:', error);
      return null;
    }
  }
}
